import { PencilSquareIcon, StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
} from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect } from 'react';
import { Controller, FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import toast from '@/app/toast.ts';
import {
  useFetchAccountRepresentativeQuery,
  usePutAccountRepresentativeMutation,
} from '@/slices/account-representative-api-slice.ts';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import type { AccountRepresentativeModel } from '../../types/account-representative.type.ts';
import AccountCategorySelector from '../account/account-category-selector.tsx';
import { FrequencySelect } from '../frequency-select.tsx';

interface AccountRepresentativeModelForm extends AccountRepresentativeModel {
  showOverride: boolean;
}

interface RepresentativeEditDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  accountRepKey?: string;
  accountId?: number;
}

const RATING_STARS = [1, 2, 3, 4, 5];

const RepresentativeEditDialog: FC<RepresentativeEditDialogProps> = ({
  accountRepKey,
  accountId,
  setIsOpen,
  isOpen,
}) => {
  const { data, isLoading: dataLoading } = useFetchAccountRepresentativeQuery(
    accountRepKey ?? skipToken,
  );
  const [put, { isLoading, isError, isSuccess }] = usePutAccountRepresentativeMutation();
  const form = useForm<AccountRepresentativeModelForm>({
    values: {
      ...data,
      showOverride: (data?.frequencyOverride?.id ?? 0) > 0,
    },
  });

  const { control, watch, handleSubmit, setValue, reset } = form;
  const ratingWatch = watch('rating') ?? 0;
  const watchCategory = watch('accountCategory');
  const watchShowOverride = watch('showOverride');
  const { currentAccount } = useSecurityContext();
  const categoryAccountId = accountId ?? data?.account?.id ?? currentAccount?.id;
  const hasCategory = (watchCategory?.id ?? 0) > 0;
  const categoryName = watchCategory?.description;
  const categoryFrequency = watchCategory?.frequency?.description;
  const representativeName = [data?.representative?.firstName, data?.representative?.lastName]
    .filter(Boolean)
    .join(' ');

  const onSubmit: SubmitHandler<AccountRepresentativeModelForm> = (
    value: AccountRepresentativeModelForm,
  ) => {
    const newValue = {
      ...value,
      frequencyOverride: value.showOverride ? value.frequencyOverride : null,
    };
    put(newValue)
      .unwrap()
      .then(() => {
        reset();
        setIsOpen(false);
      });
  };

  const handleClose = () => {
    reset();
    setIsOpen(false);
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success('Representative Saved!');
    }

    if (isError) {
      toast.error('There was an error saving your changes.');
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    setValue('showOverride', (data?.frequencyOverride?.id ?? 0) > 0);
  }, [data, setValue]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      placement='center'
      backdrop='blur'
      size='lg'
      classNames={{
        base: 'overflow-hidden border border-zinc-200 shadow-2xl dark:border-zinc-700',
        closeButton:
          'top-3 right-3 text-zinc-500 hover:bg-white/70 hover:text-zinc-950 dark:hover:bg-zinc-800 dark:hover:text-white',
      }}
    >
      <ModalContent>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader className='flex items-start gap-3 border-b border-primary-100 bg-gradient-to-br from-primary-50 via-white to-secondary-50 px-6 py-5 dark:border-zinc-700 dark:from-primary-950/60 dark:via-zinc-900 dark:to-secondary-950/30'>
              <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-900/50 dark:text-primary-300 dark:ring-primary-800'>
                <PencilSquareIcon className='size-5' />
              </span>
              <span>
                <span className='block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-secondary-600 dark:text-secondary-400'>
                  Representative settings
                </span>
                <span className='mt-1 block font-serif text-lg font-semibold text-zinc-950 dark:text-white'>
                  {representativeName || 'Edit representative'}
                </span>
                {data?.representative?.company && (
                  <span className='mt-0.5 block text-sm font-normal text-zinc-500 dark:text-zinc-400'>
                    {data.representative.company}
                  </span>
                )}
              </span>
            </ModalHeader>
            <ModalBody className='flex flex-col gap-5 px-6 py-5'>
              <div className='flex flex-col gap-1.5'>
                <Controller
                  control={control}
                  name='isFavorite'
                  render={({ field: { onChange, value } }) => (
                    <Switch color='primary' isSelected={Boolean(value)} onValueChange={onChange}>
                      Favorite
                    </Switch>
                  )}
                />
                <p className='text-sm text-default-500'>
                  Pin this representative so staff can find them faster on this account.
                </p>
              </div>

              <div className='flex flex-col gap-1.5'>
                <p className='text-sm font-medium text-zinc-800 dark:text-zinc-200'>Rating</p>
                <div className='flex flex-row gap-2'>
                  {RATING_STARS.map(star => {
                    const isFilled = star <= ratingWatch;
                    const Icon = isFilled ? StarIcon : StarIconOutline;

                    return (
                      <button
                        key={star}
                        type='button'
                        title={`Set rating to ${star}`}
                        className='rounded-md p-0.5 text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/30'
                        onClick={() => setValue('rating', star)}
                      >
                        <Icon className='size-5' />
                      </button>
                    );
                  })}
                </div>
              </div>

              <AccountCategorySelector
                accountId={categoryAccountId}
                name='accountCategory'
                required={true}
              />

              {hasCategory && (
                <div className='flex flex-col gap-1.5'>
                  <Controller
                    control={control}
                    name='showOverride'
                    render={({ field: { onChange, value } }) => (
                      <Switch color='primary' isSelected={Boolean(value)} onValueChange={onChange}>
                        Override category frequency
                      </Switch>
                    )}
                  />
                  <p className='text-sm text-default-500'>
                    Keep them in {categoryName}, but use a different meeting cadence than{' '}
                    {categoryFrequency}.
                  </p>
                </div>
              )}

              {watchShowOverride && (
                <FrequencySelect
                  label='Override meeting frequency'
                  name='frequencyOverride'
                  description='This cadence applies only to this representative. It does not change the category for anyone else.'
                />
              )}
            </ModalBody>
            <ModalFooter className='border-t border-zinc-100 px-6 py-4 dark:border-zinc-800'>
              <Button variant='flat' onPress={handleClose}>
                Cancel
              </Button>
              <Button
                color='primary'
                variant='solid'
                type='submit'
                isLoading={isLoading || dataLoading}
              >
                Save
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export default RepresentativeEditDialog;
